import { RetailerWithReward } from '@brandclub/types';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import { CampaignType } from '../types/Campaign';
import {
  BrandClubWithPromoteCampaigns,
  CheckoutEligibility,
  ProductReward,
  ProductRewardSearchResult,
} from '../types/product';
import { escapeUriPath } from './stringUtils';
import { BRANDCLUB_APP_CONFIG_ID } from './constants/Constants';
/**
 * @returns rewards associated with a product & brand
 * - used in product bullet points widget
 * - return rewards only if it's > 0
 */
export const getProductPageRewards = ({
  productEntity,
  brandEntity,
  brandclub,
  retailers,
}: {
  productEntity: any;
  brandEntity: any;
  brandclub: any;
  retailers?: Partial<RetailerWithReward>[];
}) => {
  const rewards: Partial<Record<CampaignType, number>> = {};
  const sortOrder = [
    CampaignType.DailyDealsRewardCampaign,
    CampaignType.PurchaseRewardCampaign,
    CampaignType.MembershipRewardCampaign,
    CampaignType.SurveyRewardCampaign,
    CampaignType.AffiliateRewardCampaign,
    CampaignType.ContentRewardCampaign,
    CampaignType.ReferralRewardCampaign,
    CampaignType.RetailerConnectRewardCampaign,
  ];
  const rewardTypeToCampaignMapping: Record<string, CampaignType> = {
    join: CampaignType.MembershipRewardCampaign,
    survey: CampaignType.SurveyRewardCampaign,
    content: CampaignType.ContentRewardCampaign,
    customerReferral: CampaignType.ReferralRewardCampaign,
  };
  // product rewards
  if (Array.isArray(productEntity?.campaignRewards)) {
    for (const campaignReward of productEntity.campaignRewards) {
      if (typeof campaignReward?.rewardAmount === 'number' && typeof campaignReward?.campaignType === 'string') {
        rewards[campaignReward.campaignType as CampaignType] = Math.max(
          rewards?.[campaignReward.campaignType as CampaignType] || 0,
          campaignReward.rewardAmount,
        );
      }
    }
  }
  // brand rewards
  if (typeof brandEntity?.rewardAmounts === 'object') {
    const rewardAmounts: [string, any][] = Object.entries(brandEntity.rewardAmounts);
    for (const [key, value] of rewardAmounts) {
      if (Object.keys(rewardTypeToCampaignMapping).includes(key) && typeof value?.rewardAmount === 'number') {
        rewards[rewardTypeToCampaignMapping[key] as CampaignType] = value.rewardAmount;
      }
    }
  }
  // brandclub reward
  if (typeof brandclub?.availableSurveyRewardAmount === 'number') {
    rewards[CampaignType.SurveyRewardCampaign] = brandclub.availableSurveyRewardAmount;
  }
  // retailer connect reward
  const connectRewardAmount = (retailers || []).reduce(
    (acc, retailer) => acc + (retailer?.retailerConnectRewardAmount ?? 0),
    0,
  );
  if (connectRewardAmount) {
    rewards[CampaignType.RetailerConnectRewardCampaign] = connectRewardAmount;
  }

  return sortOrder
    .filter((rewardType) => !!rewards?.[rewardType])
    .map((rewardType) => ({
      rewardType,
      rewardAmount: rewards[rewardType] as number,
    }));
};

export const buildRewardList = (
  rewards: ProductReward[],
  brandclub: BrandClubWithPromoteCampaigns | undefined,
  customerSpendByBrand?: {
    brandIds: number[];
    brandclubIds: string[];
    spendByBrandId: {
      [brandId: number]: {
        brandId: number;
        brandName: string;
        brandclubId?: string;
        spend: number;
      };
    };
    loading: boolean;
  },
) => {
  const mapList: { id: CampaignType; amount: number; subTitle: string }[] = [
    {
      id: CampaignType.PurchaseRewardCampaign,
      amount: 0,
      subTitle: 'for purchasing product',
    },
    {
      id: CampaignType.DailyDealsRewardCampaign,
      amount: 0,
      subTitle: 'for instant purchase',
    },
  ];
  if (customerSpendByBrand && brandclub && customerSpendByBrand.brandclubIds.indexOf(brandclub.brandclubId) >= 0) {
    mapList.push({
      id: CampaignType.SurveyRewardCampaign,
      amount: 0,
      subTitle: 'for completing surveys',
    });
  }
  mapList.push({
    id: CampaignType.AffiliateRewardCampaign,
    amount: 0,
    subTitle: 'affiliate commission',
  });
  mapList.push({
    id: CampaignType.ContentRewardCampaign,
    amount: 0,
    subTitle: 'in content rewards',
  });

  let res: typeof mapList = [];
  const maxByType: Record<string, any> = {};

  rewards
    .filter(({ campaignStatus }) => campaignStatus === 'live')
    .forEach((rewardBase) => {
      // We want to blend purchase and limitedTimePurchaseRewardCampaign
      const reward = { ...rewardBase };

      const rewardFound = mapList.find((item) => item.id === reward.campaignType);

      if (rewardFound) {
        if (maxByType[reward.campaignType]) {
          // if this reward amount is higher, replace
          if (reward.rewardAmount > maxByType[reward.campaignType].amount) {
            maxByType[reward.campaignType] = {
              ...rewardFound,
              amount: reward.rewardAmount,
            };
          }
        } else {
          maxByType[reward.campaignType] = {
            ...rewardFound,
            amount: reward.rewardAmount,
          };
        }
      }
    });

  if (brandclub) {
    const surveyAmount = _get(brandclub, ['availableSurveyRewardAmount'], 0);
    const contentAmount = _get(brandclub, ['availableContentRewardAmount'], 0);
    const joinClubReward = brandclub?.campaignRewards?.find(
      (c) => c.campaignType === CampaignType.MembershipRewardCampaign,
    );

    if (joinClubReward && !brandclub.isMember) {
      maxByType.promoteBrandClubRewardCampaign = {
        id: CampaignType.MembershipRewardCampaign,
        amount: joinClubReward.rewardAmount,
        subTitle: 'for joining the club',
      };
    }

    if (surveyAmount) {
      maxByType.surveyRewardCampaign = {
        id: CampaignType.SurveyRewardCampaign,
        amount: surveyAmount,
        subTitle: 'for completing surveys',
      };
    }

    if (contentAmount) {
      maxByType.contentRewardCampaign = {
        id: CampaignType.ContentRewardCampaign,
        amount: contentAmount,
        subTitle: 'in content rewards',
      };
    }
  }

  res = _orderBy(Object.values(maxByType), ['amount'], ['desc']);
  return res;
};

export const getRewardTextFromCampaignType = (campaignType: CampaignType): string => {
  switch (campaignType) {
    case CampaignType.DailyDealsRewardCampaign:
      return 'Daily Deals reward';
    case CampaignType.PurchaseRewardCampaign:
      return 'Purchase reward';
    case CampaignType.MembershipRewardCampaign:
      return 'Membership reward';
    case CampaignType.SurveyRewardCampaign:
      return 'Survey reward';
    case CampaignType.AffiliateRewardCampaign:
      return 'Affiliate reward';
    case CampaignType.ContentRewardCampaign:
      return 'Content reward';
    case CampaignType.ReferralRewardCampaign:
      return 'Referral reward';
    case CampaignType.RetailerConnectRewardCampaign:
      return 'Connect reward';
    default:
      return '';
  }
};

export const getPurchaseRewardAmountForProduct = (product: ProductRewardSearchResult) => {
  if (product.scores?.maxPurchaseRewardPercent && product.retailPrice) {
    return +((product.scores.maxPurchaseRewardPercent / 100) * product.retailPrice).toFixed(2);
  }
  // Base case: just purchase reward amount is set
  return product.scores?.maxPurchaseRewardAmount || 0;
};

export const isProductEligibleForCheckout = (
  product: ProductRewardSearchResult,
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both',
): boolean => {
  const isOutOfStock = isProductOutOfStock(product, checkoutAllowedOfferType);

  return (
    !isOutOfStock && product.isLinkedToInventory !== false && isServerSideCheckoutEligible(product.checkoutEligibility)
  );
};

export const isProductOutOfStock = (
  product: Partial<ProductRewardSearchResult>,
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both',
): boolean => {
  let isOutOfStock = !product.isAvailable;
  if (checkoutAllowedOfferType === 'firstPartyOnly' && !product.hasFirstPartyOffer) {
    // isOutOfStock = true; // we don't want to honor this flag for now
    console.warn(`1p is OOS: ${product.title}`);
  } else if (product.offers && product.offers.length > 0) {
    isOutOfStock = product.offers.every((offer) => offer.availabilityStatus === 'OutOfStock');
  }
  return isOutOfStock;
};

export const isServerSideCheckoutEligible = (checkoutEligibility: CheckoutEligibility | undefined): boolean => {
  return checkoutEligibility === CheckoutEligibility.SERVER_SIDE;
};

export const getAddToBagText = (
  product: ProductRewardSearchResult,
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both',
  disabledCheckout?: boolean,
): string => {
  return isProductEligibleForCheckout(product, checkoutAllowedOfferType) && !disabledCheckout ? '+ Add' : 'View';
};

interface GetProductUrlParams {
  title?: string;
  stacklineSku: string;
  urlSearchParams: URLSearchParams;
  dtcProductPageUrl?: string;
  storeType?: string;
  appConfigId?: string;
}
export const getProductUrl = (params: GetProductUrlParams, targetUrlOverride?: string) => {
  if (targetUrlOverride) {
    return targetUrlOverride;
  }
  if (params.storeType === 'CustomDTCStore' && !params.dtcProductPageUrl) {
    return `#`;
  }
  // On Brandclub marketplace, always link to internal PDP — don't redirect to external DTC store URLs
  const isBrandclubApp = params.appConfigId === BRANDCLUB_APP_CONFIG_ID;
  if (params.dtcProductPageUrl && !isBrandclubApp) {
    return params.dtcProductPageUrl;
  }

  const hasSearchParams = params.urlSearchParams.toString().length > 0;
  let url = `/${escapeUriPath(params.title?.substring(0, 40), '-')}/p/${params.stacklineSku}/product`;
  if (hasSearchParams) {
    url += `?${params.urlSearchParams.toString()}`;
  }
  return url;
};

interface GetProductUrlV2Params {
  title?: string;
  stacklineSku: string;
  urlSearchParams: URLSearchParams;
  retailerProductPageUrl?: string;
  storeType?: string;
  appConfigId?: string;
}
/**
 * Catalog-V2 variant of {@link getProductUrl}. V2 products carry a
 * `retailerProductPageUrl` (resolved from the catalog's primary channel — the
 * brand's own DTC store), so this links straight to it instead of the V1-named
 * `dtcProductPageUrl`. Falls back to the internal Brandclub PDP when no external
 * URL is available or on the Brandclub marketplace.
 */
export const getProductUrlV2 = (params: GetProductUrlV2Params) => {
  if (params.storeType === 'CustomDTCStore' && !params.retailerProductPageUrl) {
    return `#`;
  }
  // On Brandclub marketplace, always link to the internal PDP — never an external store URL.
  const isBrandclubApp = params.appConfigId === BRANDCLUB_APP_CONFIG_ID;
  if (params.retailerProductPageUrl && !isBrandclubApp) {
    return params.retailerProductPageUrl;
  }

  const hasSearchParams = params.urlSearchParams.toString().length > 0;
  let url = `/${escapeUriPath(params.title?.substring(0, 40), '-')}/p/${params.stacklineSku}/product`;
  if (hasSearchParams) {
    url += `?${params.urlSearchParams.toString()}`;
  }
  return url;
};

const isInstantPurchaseRewardAvailable = (reward: ProductReward) => {
  const isInstantPurchase =
    reward.campaignType === CampaignType.DailyDealsRewardCampaign ||
    (reward.rewardUnlockStrategy === 'instant' && reward.campaignType === CampaignType.PurchaseRewardCampaign);
  const isLive = reward.campaignStatus === 'live';
  return isInstantPurchase && isLive;
};

const isPurchaseRewardAvailable = (reward: ProductReward) => {
  const isPurchase = reward.campaignType === CampaignType.PurchaseRewardCampaign;
  const isLive = reward.campaignStatus === 'live';
  return isPurchase && isLive;
};

export const getPurchaseRewardAmountForProductV2 = (p: ProductRewardSearchResult | undefined) => {
  let rewardType: 'instant' | 'purchase' = 'purchase';
  let rewardAmount = 0;
  let rewardPercent = 0;

  const liveInstantPurchase = p?.campaignRewards?.filter((reward) => {
    return isInstantPurchaseRewardAvailable(reward);
  });

  if (liveInstantPurchase && liveInstantPurchase.length > 0) {
    const maxInstantPurchaseReward = liveInstantPurchase.reduce((prev, current) => {
      if (current.rewardAmount > prev.rewardAmount) {
        return current;
      }

      return prev;
    });

    rewardType = 'instant';
    rewardAmount = maxInstantPurchaseReward.rewardAmount;
    rewardPercent = maxInstantPurchaseReward.rewardPercent || 0;
  } else {
    const livePurchase = p?.campaignRewards?.filter((reward) => {
      return isPurchaseRewardAvailable(reward);
    });

    if (livePurchase && livePurchase.length > 0) {
      const maxPurchaseReward = livePurchase.reduce((prev, current) => {
        if (current.rewardAmount > prev.rewardAmount) {
          return current;
        }

        return prev;
      });

      rewardAmount = maxPurchaseReward.rewardAmount;
      rewardPercent = maxPurchaseReward.rewardPercent || 0;
    }
  }

  return {
    type: rewardType,
    amount: rewardAmount,
    percent: rewardPercent,
  };
};

export const getSortedBuyBoxProducts = (productsToList: ProductRewardSearchResult[]) => {
  if (!productsToList) return [];

  // Sort products by Add > Price > Rewards > push Out of Stock to bottom
  const sortedProducts = [...productsToList].sort((a, b) => {
    if (isProductOutOfStock(a) && !isProductOutOfStock(b)) return 1;
    if (!isProductOutOfStock(a) && isProductOutOfStock(b)) return -1;

    // check server only so that it is sync between web app and mobile app
    if (
      a.checkoutEligibility === CheckoutEligibility.SERVER_SIDE &&
      b.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE
    )
      return -1;
    if (
      a.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE &&
      b.checkoutEligibility === CheckoutEligibility.SERVER_SIDE
    )
      return 1;

    if (a.retailPrice && b.retailPrice) {
      const aReward = getPurchaseRewardAmountForProductV2(a);
      const bReward = getPurchaseRewardAmountForProductV2(b);
      if (a.retailPrice - aReward.amount < b.retailPrice - bReward.amount) return -1;
      if (a.retailPrice - aReward.amount > b.retailPrice - bReward.amount) return 1;
    }

    if (a.campaignRewards && b.campaignRewards) {
      const aReward = getPurchaseRewardAmountForProductV2(a);
      const bReward = getPurchaseRewardAmountForProductV2(b);
      if (aReward.amount > bReward.amount) return -1;
      if (aReward.amount < bReward.amount) return 1;
    }

    return 0;
  });
  return sortedProducts;
};
